/**
 * Talking to the game server.
 *
 * Control messages are JSON, chunk payloads are binary — the encoding lives in
 * `@lofi/shared` so both sides use the same code.
 */

import type { FlockWarningMessage, LoomView } from '@lofi/shared';
import {
  PROTOCOL_VERSION,
  type MoveItemMessage,
  type MoveHearthItemMessage,
  type MoveChestItemMessage,
  type DiscardItemMessage,
  decodeChunkFrame,
  type BlockChange,
  type BlockEditMessage,
  type ChunkCoord,
  type DecodedChunkFrame,
  type HelloMessage,
  type PlayerStateMessage,
  type ServerMessage,
  type WeatherSnapshot,
  type WorldClockSnapshot,
  type SelectSlotMessage,
  type Stack,
  type EatItemMessage,
  type UseBlockMessage,
  type SpearMessage,
  type StrikeMessage,
  type ShearMessage,
  type FeedMessage,
  type GiveMessage,
  type LeadMessage,
  type UnleadMessage,
  type CarryMessage,
  type ReleaseMessage,
  type WakeMessage,
  type AsleepMessage,
  type AskNearbyMaterialsMessage,
  type AskHearthContentsMessage,
  type MoveBurItemMessage,
  type CreaturesMessage,
  type PlayersMessage,
  type CraftMessage,
  type Vitals,
} from '@lofi/shared';

export interface Connection {
  requestChunks(chunks: readonly ChunkCoord[]): void;
  /** Tell the server where we are — it cannot check reach without this (T52). */
  sendPlayerState(state: Omit<PlayerStateMessage, 'type'>): void;
  /** Ask to place or break a block. `block` is a key; `'air'` breaks (T52). */
  sendBlockEdit(x: number, y: number, z: number, block: string): void;
  /** Ask to hold a different one of the ten on-hand slots (protocol 9). */
  sendSelectSlot(slot: number): void;
  /**
   * 🎒 Ask the server to move some of one slot into another (protocol 10).
   *
   * ⚠️ **Nothing changes on screen until the server answers**, exactly as with
   * `sendSelectSlot`. One source of truth is what stops the pack on screen
   * drifting from the pack in the database.
   */
  sendMoveItem(from: number, to: number, count: number): void;
  /**
   * 🗑️ Ask the server to throw part of a slot away (protocol 11).
   *
   * ⚠️ **There is no undo.** The interface is what protects this, not the wire.
   */
  /** 🔥 Move a stack between the pack and a hearth — T128, protocol 16. */
  sendMoveHearthItem(
    at: { x: number; y: number; z: number },
    from: { where: 'hearth' | 'player'; slot: number },
    to: { where: 'hearth' | 'player'; slot: number },
    count: number,
  ): void;
  /** 📦 Move a stack between the pack and a chest — T135, protocol 20. */
  sendMoveChestItem(
    at: { x: number; y: number; z: number },
    from: { where: 'chest' | 'player'; slot: number },
    to: { where: 'chest' | 'player'; slot: number },
    count: number,
  ): void;
  sendDiscardItem(from: number, count: number): void;
  /**
   * 🍇 Ask the server to eat one of what is in a slot (protocol 12).
   *
   * ⚠️ **Nothing changes on screen until the server answers**, like every other
   * request here. The client says what it can answer for itself — *not hungry*,
   * *that is not food* — **before** sending, and once it has sent, the bars it
   * draws are the server's.
   */
  sendEatItem(slot: number): void;
  /**
   * 🫐 Ask the server to use the block under the crosshair (protocol 12).
   *
   * Today: picking berries off the lyng. **No block changes**, so nothing comes
   * back through `onBlockUpdate` — what arrives is a new `inventory`, or an
   * `edit_rejected` saying why not.
   */
  sendUseBlock(x: number, y: number, z: number): void;
  /**
   * 🔱 **Thrust the spear at a fish** — T221, D115, protocol 31.
   *
   * 🔴 **One creature id and nothing else.** Not what was caught, not that it
   * was in range — **the server re-runs the same aim and refuses a name that
   * does not survive it.** A client that could name its own target could name a
   * fish on the other side of the bay.
   */
  sendSpear(id: string): void;
  /**
   * 🔪 **Strike that sheep with your knife** — D125, protocol 38. One id; the
   * server decides whether it lands, whose sheep it is, and whether it dies.
   */
  sendStrike(id: string): void;
  /**
   * ✂️ **Shear that sheep** — D126, protocol 40. One id; the server decides
   * whether a knife is in hand, whether the month lets you pluck, and whose it is.
   */
  sendShear(id: string): void;
  /**
   * 🌱 **Feed that sheep turf** — D130, protocol 44. One id; the server decides
   * whether it is December, whether it is yours and whether it is penned.
   */
  sendFeed(id: string): void;
  /** 🎁 **An admin asks for an item** — protocol 47. The server decides. */
  sendGive(item: string, count: number): void;
  /** 🪢 **Put a lead on that sheep** — D128, protocol 42. */
  sendLead(id: string): void;
  /** 🪢 **Let that sheep off its lead** — D128, protocol 42. */
  sendUnlead(id: string): void;
  /** 🎣 **Cast the float where I am looking** — D129, protocol 43. */
  sendCast(): void;
  /** 🎣 **Pull** — land it if it is biting, otherwise wind the float in. */
  sendReel(): void;
  /**
   * 🙌 **Pick that sheep up** — T261, D123, protocol 37.
   *
   * 🔴 **One creature id and nothing else**, exactly as the spear sends. **The
   * server re-runs `carryTargetFrom` and refuses a name that does not survive
   * it** — a client that could name its own target could name a sheep on the
   * other side of the valley.
   */
  sendCarry(id: string): void;
  /**
   * 🙌 **Put down whatever you are carrying** — T261, protocol 37.
   *
   * 🔴 **It carries NOTHING.** Where the sheep lands is worked out by the server
   * from where it believes you stand and which way you face; **a position here
   * would be a client naming a place**, and a sheep posted through a wall is
   * what that buys.
   */
  sendRelease(): void;
  /** 🛏️ **Get up** — D133, protocol 50. Nothing on it; the server knows the bed. */
  sendWake(): void;
  /** 🔨 Ask the server to make something — T130 slice 3, D95. */
  sendCraft(recipe: string, at: { x: number; y: number; z: number } | null): void;
  /**
   * 🔨 **Ask what the bench can see from here** — T172, D111, protocol 25.
   *
   * ⚠️ **It names nothing.** The server measures from where it thinks we are,
   * which is the position a `craft` is judged from, so the screen and the
   * authority cannot answer differently.
   */
  sendAskNearbyMaterials(): void;
  /**
   * 🍲 **Ask what a hearth holds right now** — T222, protocol 32.
   *
   * 🔴 **Sent on a beat while the hearth screen is open**, because the fire is
   * the one container that changes while you are looking at it: a fish cooks in
   * about 19 seconds, and a screen that never moves reads as a fire that does
   * not work.
   */
  sendAskHearthContents(at: { x: number; y: number; z: number }): void;
  /** 🥫🔥 Move between a búr's food store and its own fire — T222c, protocol 34. */
  sendMoveBurItem(
    at: { x: number; y: number; z: number },
    from: { where: 'store' | 'fire'; slot: number },
    to: { where: 'store' | 'fire'; slot: number },
    count: number,
  ): void;
  close(): void;
}

export interface ConnectionHandlers {
  onWelcome(
    spawn: { x: number; y: number; z: number },
    worldgenVersion: number,
    /** Whether the server is sending the flat debug view — its answer, not our request. */
    flat: boolean,
    /** Who the server thinks we are, or null for an unidentified connection (T52). */
    player: { readonly displayName: string; readonly admin?: true } | null,
    /**
     * 🔐 Where to go and sign in, or `undefined` where this server does not
     * expect anybody in particular (T88). **The server's finished URL** — see
     * `signInIfNobody` in `main.ts` for what is done with it.
     */
    signIn: string | undefined,
    /** 🪢 This connection's own id, or `null` from an older server — protocol 42. */
    you: string | null,
  ): void;
  onChunk(chunk: DecodedChunkFrame): void;
  /**
   * Blocks the server says have changed (T52).
   *
   * ⚠️ **The only thing that changes a block on screen.** Our own edits arrive
   * back through here too — the client does not predict them, so there is no
   * rollback path to get wrong and no way for one player's world to drift from
   * everybody else's.
   */
  onBlockUpdate(edits: readonly BlockChange[]): void;
  /**
   * An edit the server refused, as a **key** into the language file (protocol 8).
   *
   * ⚠️ Not a finished sentence: the server has no idea what language this player
   * reads, and should not have to (T124).
   */
  onEditRejected(reasonKey: string, values?: Readonly<Record<string, string | number>>): void;
  /**
   * What time it is in the world (T103).
   *
   * Called on the welcome and then whenever the server resyncs. ⚠️ **The server
   * is the only clock**: there is one for the whole world (D41), and a client
   * that started its own would drift away from everybody else's seasons.
   */
  onClock(clock: WorldClockSnapshot): void;
  /**
   * What the weather is doing where we are standing (T103 piece ④).
   *
   * Called on the welcome and then on every resync. ⚠️ **`null` is a real
   * answer** — the server has not drawn a year — and it means *keep the default*,
   * never *the weather is calm*.
   */
  onWeather(weather: WeatherSnapshot | null): void;
  /**
   * 🎒 What we are carrying (protocol 9).
   *
   * ⚠️ **The whole inventory, every time** — never a delta. The server is the
   * only thing that knows what is in the pack, and a client holding its own
   * copy would eventually disagree with it in a way nobody could see.
   */
  onInventory(stacks: readonly Stack[], selected: number): void;
  /**
   * 🔥 What a hearth holds — T128, protocol 16.
   *
   * ⚠️ **Numbers and a flag, never a sentence.** The server does not know what
   * language this player reads, so the client writes the words.
   */
  onHearthContents(
    at: { x: number; y: number; z: number },
    slots: readonly number[],
    lit: boolean,
    hoursLeft: number,
    /** 🪵 Which wood is in each wood slot — protocol 19, empty from an older server. */
    woodItems: readonly (string | null)[],
    /** 🏠 Whether this is where you would wake — protocol 22, false from an older server. */
    home: boolean,
    /** 🔥 The pit-house oven — protocol 52: wood only. False from an older server. */
    oven: boolean,
  ): void;
  /**
   * 📦 What a chest holds — T135, protocol 20.
   *
   * ⚠️ **Full stacks, not counts.** A chest slot can hold anything, so a number
   * alone says nothing about what it is a number OF — which is the mistake the
   * hearth’s message had to be corrected for one protocol earlier.
   */
  onChestContents(
    at: { x: number; y: number; z: number },
    stacks: readonly Stack[],
    /** 🏠 27 for a chest, 54 for a storehouse — protocol 23. */
    capacity: number,
    /**
     * 🪧 **What this container is called** — protocol 24, an `en.json` key.
     *
     * ⚠️ **`null` from a server too old to say**, and the screen falls back to
     * guessing from the capacity — which is what it always did, and is what
     * called the skemma a búr.
     */
    title: string | null,
    /**
     * 🥫🔥 **This container's fire, or `null`** — protocol 33, D121.
     *
     * 🔴 **Only a búr has one.** `null` for every chest and skemma, and for a
     * server that has not caught up — **and the screen draws the same page
     * either way**, which is the page it has always had.
     */
    fire: {
      readonly slots: readonly number[];
      readonly slotItems: readonly (string | null)[];
      readonly lit: boolean;
      readonly hoursLeft: number;
    } | null,
    /** 🧵 A vefstofa's spinning, or `null` — protocol 41, D127. */
    spin: 'spinning' | 'idle' | 'full' | null,
    /** 🧵 A loom's weaving, or `null` — protocol 53, D136. */
    loom: LoomView | null,
  ): void;
  /**
   * 🔨 **What the bench can see, beyond the pack** — T172, D111, protocol 25.
   *
   * 🔴 **For DISPLAY only.** The server recomputes every input when the craft
   * arrives, so this can go stale and cause a refusal, and can never cause a
   * craft that spends something that is not there.
   *
   * ⚠️ **Pooled, with NO slot numbers**, because `canMake` counts by item key
   * and never looks at a slot. **Do not hand it to anything that moves an
   * item** — a pack's slots 34–37 are clothing and a búr's are storage.
   */
  /**
   * 🧍 **Where everybody else is** — T173, protocol 26.
   *
   * ⚠️ **The list is the whole truth, and an EMPTY one is a real answer**: it
   * means everybody else has gone, and the bodies go with them. *A frozen body
   * and a departed body look identical* — see `players.ts`.
   *
   * ⚠️ **We are never in it.** The server sends everyone but the recipient, so
   * no client has to find itself.
   */
  onPlayers(players: PlayersMessage['players']): void;
  /**
   * 🐟 **Every animal near you, and what each one is DOING** — protocol 30,
   * T228, D117.
   *
   * ⚠️ **An animal not in the list is GONE**, immediately and with no grace
   * period — the same rule as a body. *A frozen creature and a departed one
   * look identical.*
   *
   * 🔴 **`doing` carries the NAME of the behaviour in charge**, which is what
   * makes an F8 report able to say *"it was `Fleeing` when it did that."*
   */
  onCreatures(message: CreaturesMessage): void;
  /**
   * 🙌 **What is in YOUR OWN arms** — T261, protocol 37.
   *
   * 🔴 **`onPlayers` cannot tell you this**: that list never includes the
   * viewer, deliberately. See `CarryingMessage`.
   */
  onCarrying(kind: string | null, lamb: number | undefined): void;
  /** 🛏️ D133, protocol 50 — asleep in this bed, or awake and where to stand. */
  onAsleep(message: AsleepMessage): void;
  /** 🐑⚠️ Protocol 48 — where YOUR starving sheep are. Empty: none are. */
  onFlockWarning(flocks: FlockWarningMessage['flocks']): void;
  /** 🎣 Every float in the water — protocol 43, D129. */
  onFloats(floats: readonly { by: string; x: number; y: number; z: number; dip: boolean }[]): void;
  onNearbyMaterials(
    stacks: readonly { itemKey: string; count: number }[],
    /** How many containers those came out of. Zero is a real answer. */
    containers: number,
  ): void;
  /**
   * 🍖 How our own body is doing — health, food, and why (protocol 12).
   *
   * ⚠️ **A correction, not the thing that moves the bar.** It arrives on the
   * server's 30-second save beat; the client runs the same `stepVitals` from
   * `@lofi/shared` every frame in between, so the bar is smooth and the numbers
   * still come from one place. A correction that is ever LARGE is a real
   * disagreement worth seeing, not a rounding difference.
   */
  /**
   * 💀 **You died, and the server has already moved you** — protocol 21, D21.
   *
   * ⚠️ **The emptied pack arrives separately**, on the ordinary `inventory`
   * message, because there must be one way a pack changes and not two.
   *
   * @param atHome whether they woke at their own hearth or back at the
   *   beginning — **not derivable from the coordinates**, and the two are
   *   different sentences on screen.
   */
  onDied(x: number, y: number, z: number, atHome: boolean): void;

  /**
   * 🏠 **Where this player wakes up** — protocol 29, T201.
   *
   * ⚠️ **Arrives on connect AND every time it moves**, because lighting a
   * hearth moves it. 🔴 **`null` is an ordinary answer**: somebody who has
   * never lit a hearth has no home, and on development that is everybody until
   * they light one. **Draw it as a reason, never as a button that does
   * nothing.**
   */
  onHome(at: { x: number; y: number; z: number } | null): void;

  onVitals(
    vitals: Vitals,
    reasonKey: string | null,
    /** 🥶 What the food drain is multiplied by — 1 is comfortable (T131). */
    exposure: number,
    /** Which of cold, wind and wet are costing something, worst first. */
    biting: readonly string[],
  ): void;
  onStatus(text: string): void;
}

export interface ConnectOptions {
  /** Ask for the flat-terrain debug view (T49). */
  readonly flat: boolean;
  /** A one-shot play token from the website, if this is a first connection. */
  readonly token?: string | undefined;
  /** A game session from a previous exchange, for a reconnect. */
  readonly session?: string | undefined;
}

/** Where the game session is kept between reconnects and reloads (T52). */
const GAME_SESSION_KEY = 'lofi.gameSession';

/**
 * The play token from the website's Play button — `?t=…` — **removed from the
 * address bar as it is read** (T52).
 *
 * ⚠️ Stripping it immediately is the point. It is single use and two minutes
 * old at most, but leaving it in the URL puts it in the browser history and in
 * anything the player copies out of the address bar, and it is the one value in
 * this system that grants an identity.
 */
export function takePlayToken(): string | undefined {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('t');
  if (token === null || token === '') return undefined;

  params.delete('t');
  const query = params.toString();
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${query === '' ? '' : `?${query}`}`,
  );

  return token;
}

/**
 * The game session from a previous exchange, if there is one.
 *
 * `sessionStorage`, not `localStorage`: it is scoped to the tab, cleared when
 * the tab closes, and never shared with another origin. A credential that
 * outlives the window it was issued to is a credential nobody remembers having.
 */
export function storedGameSession(): string | undefined {
  try {
    return window.sessionStorage.getItem(GAME_SESSION_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

export function rememberGameSession(id: string): void {
  try {
    window.sessionStorage.setItem(GAME_SESSION_KEY, id);
  } catch {
    /* Storage disabled. Identity then lasts exactly one connection, which
       costs a reconnect after the flat switch — not a broken game. */
  }
}

/**
 * 🔐 **Whether this tab has already been sent away to sign in** — T88.
 *
 * ⚠️ **Beside the game session on purpose**, because it is the same tab and the
 * same storage, and *a second way in forgets what the first one does* is the
 * fault this repo has recorded six times. **Everything this client keeps about
 * who you are is written and read in these few lines.**
 *
 * 🔴 **It lives in `sessionStorage` deliberately.** A new tab genuinely deserves
 * a fresh attempt — the marker exists to stop one tab looping, not to remember
 * a failure for ever.
 *
 * ⚠️ **Storage can throw**, and when it does this answers `false`: **one wasted
 * redirect is a far better failure than never offering the way back at all.**
 */
const SIGN_IN_TRIED_KEY = 'lofi.signInTried';

export function signInAttempted(): boolean {
  try {
    return window.sessionStorage.getItem(SIGN_IN_TRIED_KEY) !== null;
  } catch {
    return false;
  }
}

export function rememberSignInAttempt(): void {
  try {
    window.sessionStorage.setItem(SIGN_IN_TRIED_KEY, '1');
  } catch {
    /* See above: without the marker the loop guard is gone, so the only other
       protection is that a failed hand-over usually fails the same way twice
       and the player can close the tab. Not worth breaking the game over. */
  }
}

export function forgetSignInAttempt(): void {
  try {
    window.sessionStorage.removeItem(SIGN_IN_TRIED_KEY);
  } catch {
    /* Nothing to forget if nothing could be remembered. */
  }
}

export function connect(
  url: string,
  handlers: ConnectionHandlers,
  options: ConnectOptions,
): Connection {
  const socket = new WebSocket(url);
  socket.binaryType = 'arraybuffer';

  socket.addEventListener('open', () => {
    const hello: HelloMessage = {
      type: 'hello',
      protocolVersion: PROTOCOL_VERSION,
      ...(options.flat ? { flat: true } : {}),
      /* ⚠️ Token first, session second, never both. A play token is single use;
         spending one on a reconnect that an existing session would have covered
         throws away the credential for no reason. */
      ...(options.token !== undefined
        ? { token: options.token }
        : options.session !== undefined
          ? { session: options.session }
          : {}),
    };
    socket.send(JSON.stringify(hello));
  });

  socket.addEventListener('message', (event: MessageEvent<string | ArrayBuffer>) => {
    if (event.data instanceof ArrayBuffer) {
      try {
        handlers.onChunk(decodeChunkFrame(event.data));
      } catch (err) {
        /* A malformed frame is a bug worth seeing, not worth crashing over —
           the rest of the world keeps streaming. */
        handlers.onStatus(`bad chunk frame: ${err instanceof Error ? err.message : String(err)}`);
      }
      return;
    }

    const message = JSON.parse(event.data) as ServerMessage;
    if (message.type === 'welcome') {
      /* ⚠️ If the server regenerates terrain with a different worldgenVersion
         than the client last saw, anything built on the old terrain may now be
         floating or buried. Nothing is built yet, so this is only reported —
         but the check belongs here from the start (standards §7). */
      /* ⚠️ Stored before the handler runs. `onWelcome` may rebuild the world,
         and a reconnect that happened before the credential was saved would
         arrive unidentified — which looks like the token having failed. */
      if (message.gameSession !== undefined) rememberGameSession(message.gameSession);
      /* Before `onWelcome`, which rebuilds the world: the HUD should never have
         a frame where the world exists and the date does not. */
      handlers.onClock(message.clock);
      handlers.onWeather(message.weather ?? null);
      handlers.onWelcome(
        message.spawn,
        message.worldgenVersion,
        message.flat === true,
        message.player ?? null,
        message.signIn,
        message.you ?? null,
      );
    } else if (message.type === 'chunk_error') {
      handlers.onStatus(`server could not generate ${message.chunk.cx},${message.chunk.cz}`);
    } else if (message.type === 'block_update') {
      handlers.onBlockUpdate(message.edits);
    } else if (message.type === 'edit_rejected') {
      handlers.onEditRejected(message.reasonKey, message.reasonValues);
    } else if (message.type === 'clock') {
      handlers.onClock(message.clock);
    } else if (message.type === 'weather') {
      handlers.onWeather(message.weather);
    } else if (message.type === 'inventory') {
      handlers.onInventory(message.stacks, message.selected);
    } else if (message.type === 'hearth_contents') {
      handlers.onHearthContents(
        message.at,
        message.slots,
        message.lit,
        message.hoursLeft,
        /* 🔴 A server that sends no names leaves every slot EMPTY on screen —
           protocol 32 removed the panel's fallback, and removing it was the
           point. It guessed from the slot NUMBER, which can only be right while
           a group of slots holds one known thing, and the output shelf now holds
           four cooked fish as well as charcoal. ⚠️ An empty box is honest; a
           confidently wrong one is not. */
        message.slotItems ?? [],
        /* 🏠 An older server never says; "not your home" is the safe reading,
           because the line it draws tells you how to make it so. */
        message.home ?? false,
        /* 🔥 D135 — absent for a hearth. */
        message.oven === true,
      );
    } else if (message.type === 'died') {
      /* 💀 Protocol 21. ⚠️ **Not a correction to reconcile — an instruction.**
         The server has moved this player; a client that argued would walk its
         own corpse back to where it died. */
      handlers.onDied(message.x, message.y, message.z, message.atHome);
    } else if (message.type === 'home') {
      /* 🏠 Protocol 29. ⚠️ An older server never sends one, so the map's
         button stays in its "no home yet" state — which is honest, because a
         server that cannot say where home is cannot take you there. */
      handlers.onHome(message.at);
    } else if (message.type === 'chest_contents') {
      handlers.onChestContents(
        message.at,
        message.stacks,
        message.capacity ?? 27,
        message.title ?? null,
        /* 🥫🔥 Protocol 33: a búr's fire. Absent for every other container, and
           for a server that has not caught up — the same drawing either way. */
        message.fire ?? null,
        /* 🧵 Protocol 41: a vefstofa's spinning. */
        message.spin ?? null,
        /* 🧵 Protocol 53: a loom's weaving. */
        message.loom ?? null,
      );
    } else if (message.type === 'players') {
      handlers.onPlayers(message.players);
    } else if (message.type === 'creatures') {
      handlers.onCreatures(message);
    } else if (message.type === 'flock_warning') {
      handlers.onFlockWarning(message.flocks);
    } else if (message.type === 'carrying') {
      /* 🐣 Protocol 49 — `lamb` is present only for a lamb. */
      handlers.onCarrying(message.kind, message.lamb);
    } else if (message.type === 'asleep') {
      handlers.onAsleep(message);
    } else if (message.type === 'floats') {
      handlers.onFloats(message.floats);
    } else if (message.type === 'nearby_materials') {
      handlers.onNearbyMaterials(message.stacks, message.containers);
    } else if (message.type === 'vitals') {
      handlers.onVitals(
        { healthPercent: message.healthPercent, foodPercent: message.foodPercent },
        message.reasonKey,
        /* ⚠️ A server one protocol old sends neither field. Defaulting to
           "comfortable" rather than throwing means an old build shows no cold
           instead of no game. */
        message.exposure ?? 1,
        message.bitingFactors ?? [],
      );
    }
  });

  socket.addEventListener('close', () => handlers.onStatus('disconnected'));
  socket.addEventListener('error', () => handlers.onStatus('connection failed'));

  return {
    requestChunks(chunks) {
      if (socket.readyState !== WebSocket.OPEN || chunks.length === 0) return;
      socket.send(JSON.stringify({ type: 'chunk_request', chunks }));
    },

    sendPlayerState(state) {
      if (socket.readyState !== WebSocket.OPEN) return;
      const message: PlayerStateMessage = { type: 'player_state', ...state };
      socket.send(JSON.stringify(message));
    },

    sendBlockEdit(x, y, z, block) {
      if (socket.readyState !== WebSocket.OPEN) return;
      const message: BlockEditMessage = { type: 'block_edit', x, y, z, block };
      socket.send(JSON.stringify(message));
    },

    sendSelectSlot(slot) {
      if (socket.readyState !== WebSocket.OPEN) return;
      const message: SelectSlotMessage = { type: 'select_slot', slot };
      socket.send(JSON.stringify(message));
    },

    sendMoveItem(from, to, count) {
      if (socket.readyState !== WebSocket.OPEN) return;
      const message: MoveItemMessage = { type: 'move_item', from, to, count };
      socket.send(JSON.stringify(message));
    },

    /**
     * 🔥 Move a stack between the pack and a hearth — T128, protocol 16.
     *
     * ⚠️ **Each end names its container.** Both start at slot 0, so a bare pair
     * of numbers would leave the server to guess which table each belongs to.
     */
    sendMoveHearthItem(at, from, to, count) {
      if (socket.readyState !== WebSocket.OPEN) return;
      const message: MoveHearthItemMessage = { type: 'move_hearth_item', at, from, to, count };
      socket.send(JSON.stringify(message));
    },

    /**
     * 📦 Move a stack between the pack and a chest — T135, protocol 20.
     *
     * ⚠️ **Each end names its container**, for the reason the hearth’s message
     * gives: both start at slot 0, so a bare pair of numbers would leave the
     * server to guess which table each belongs to.
     */
    sendMoveChestItem(at, from, to, count) {
      if (socket.readyState !== WebSocket.OPEN) return;
      const message: MoveChestItemMessage = { type: 'move_chest_item', at, from, to, count };
      socket.send(JSON.stringify(message));
    },

    sendDiscardItem(from, count) {
      if (socket.readyState !== WebSocket.OPEN) return;
      const message: DiscardItemMessage = { type: 'discard_item', from, count };
      socket.send(JSON.stringify(message));
    },

    sendEatItem(slot) {
      if (socket.readyState !== WebSocket.OPEN) return;
      const message: EatItemMessage = { type: 'eat_item', slot };
      socket.send(JSON.stringify(message));
    },

    sendUseBlock(x, y, z) {
      if (socket.readyState !== WebSocket.OPEN) return;
      const message: UseBlockMessage = { type: 'use_block', x, y, z };
      socket.send(JSON.stringify(message));
    },

    sendSpear(id) {
      if (socket.readyState !== WebSocket.OPEN) return;
      const message: SpearMessage = { type: 'spear', id };
      socket.send(JSON.stringify(message));
    },

    sendStrike(id) {
      if (socket.readyState !== WebSocket.OPEN) return;
      const message: StrikeMessage = { type: 'strike', id };
      socket.send(JSON.stringify(message));
    },

    sendShear(id) {
      if (socket.readyState !== WebSocket.OPEN) return;
      const message: ShearMessage = { type: 'shear', id };
      socket.send(JSON.stringify(message));
    },

    sendGive(item, count) {
      if (socket.readyState !== WebSocket.OPEN) return;
      const message: GiveMessage = { type: 'give', item, count };
      socket.send(JSON.stringify(message));
    },

    sendFeed(id) {
      if (socket.readyState !== WebSocket.OPEN) return;
      const message: FeedMessage = { type: 'feed', id };
      socket.send(JSON.stringify(message));
    },

    sendLead(id) {
      if (socket.readyState !== WebSocket.OPEN) return;
      const message: LeadMessage = { type: 'lead', id };
      socket.send(JSON.stringify(message));
    },

    sendCast() {
      if (socket.readyState !== WebSocket.OPEN) return;
      socket.send(JSON.stringify({ type: 'cast' }));
    },

    sendReel() {
      if (socket.readyState !== WebSocket.OPEN) return;
      socket.send(JSON.stringify({ type: 'reel' }));
    },

    sendUnlead(id) {
      if (socket.readyState !== WebSocket.OPEN) return;
      const message: UnleadMessage = { type: 'unlead', id };
      socket.send(JSON.stringify(message));
    },

    sendCarry(id) {
      if (socket.readyState !== WebSocket.OPEN) return;
      const message: CarryMessage = { type: 'carry', id };
      socket.send(JSON.stringify(message));
    },

    sendRelease() {
      if (socket.readyState !== WebSocket.OPEN) return;
      const message: ReleaseMessage = { type: 'release' };
      socket.send(JSON.stringify(message));
    },

    sendWake() {
      if (socket.readyState !== WebSocket.OPEN) return;
      const message: WakeMessage = { type: 'wake' };
      socket.send(JSON.stringify(message));
    },

    sendCraft(recipe, at) {
      if (socket.readyState !== WebSocket.OPEN) return;
      const message: CraftMessage = { type: 'craft', recipe, at };
      socket.send(JSON.stringify(message));
    },

    sendAskNearbyMaterials() {
      if (socket.readyState !== WebSocket.OPEN) return;
      const message: AskNearbyMaterialsMessage = { type: 'ask_nearby_materials' };
      socket.send(JSON.stringify(message));
    },

    sendMoveBurItem(at, from, to, count) {
      if (socket.readyState !== WebSocket.OPEN) return;
      const message: MoveBurItemMessage = { type: 'move_bur_item', at, from, to, count };
      socket.send(JSON.stringify(message));
    },

    sendAskHearthContents(at) {
      if (socket.readyState !== WebSocket.OPEN) return;
      const message: AskHearthContentsMessage = { type: 'ask_hearth_contents', at };
      socket.send(JSON.stringify(message));
    },

    close: () => socket.close(),
  };
}

/**
 * Where to reach the server.
 *
 * Derived from the page's own origin so the same build works on localhost, on
 * `dev.lofi.asgeir.is` behind the proxy, and anywhere else — no environment
 * baked into the bundle.
 */
export function serverUrl(): string {
  const override = new URLSearchParams(window.location.search).get('server');
  if (override) return override;

  const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';

  /* In production the client and server sit behind ONE origin: nginx serves the
     static bundle and proxies /ws through to the game server. That means the
     reverse proxy needs a single entry, TLS is terminated once, and there is no
     CORS or mixed-content problem to debug later.
     In dev, Vite serves on 5173 and the server runs separately on 3000. */
  return import.meta.env.DEV
    ? `${scheme}//${window.location.hostname}:3000`
    : `${scheme}//${window.location.host}/ws`;
}

/**
 * Has the flat-terrain debug view been asked for? — `?flat=1` (T49).
 *
 * Matches the existing `?radius=` and `?server=` overrides: a URL you can click
 * is a control that cannot be defeated by a keyboard layout or a browser
 * security prompt, which is why those two exist.
 *
 * `?flat=0` and `?flat=false` mean off, because a URL parameter someone typed
 * by hand should mean what it looks like it means.
 */
export function flatRequested(): boolean {
  return flatInSearch(window.location.search);
}

/** The testable half of {@link flatRequested}. */
export function flatInSearch(search: string): boolean {
  const value = new URLSearchParams(search).get('flat');
  if (value === null) return false;
  return value !== '0' && value.toLowerCase() !== 'false';
}

/**
 * The same query string with `flat` switched on or off, everything else kept.
 *
 * The toggle rewrites the address bar so that reloading, bookmarking or sending
 * someone the link keeps the view you were looking at — and so that `?radius=`
 * and `?server=` survive being flipped in and out of flat mode.
 */
export function withFlatParam(search: string, flat: boolean): string {
  const params = new URLSearchParams(search);
  if (flat) params.set('flat', '1');
  else params.delete('flat');
  const query = params.toString();
  return query === '' ? '' : `?${query}`;
}
